import { Calculator } from 'lucide-react';
import { formatRange } from './utils';

interface Ratio {
  key: string;
  label: string;
  value: number | null;
  formula: string;
  interpretation: string;
}

interface Data {
  type: string;
  period: { from: string; to: string; days: number };
  asOfDate: string;
  ratios: Ratio[];
}

function formatRatio(key: string, value: number | null): string {
  if (value === null) return '—';
  if (key.endsWith('_pct')) return `${value.toFixed(2)}%`;
  if (key === 'dso' || key === 'dpo') return `${value.toFixed(1)} days`;
  return String(value);
}

export function FinancialRatiosReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const rangeLabel = formatRange(d.period.from, d.period.to);
  const isEmpty = d.ratios.length === 0;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Calculator className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">Financial Ratios</span>
        </div>
        {rangeLabel && (
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
            {rangeLabel}
          </span>
        )}
      </div>

      {/* Ratio rows */}
      {isEmpty ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No ratios available for this period.
        </div>
      ) : (
        <div>
          {d.ratios.map((ratio) => {
            const isUnavailable = ratio.value === null;
            return (
              <div
                key={ratio.key}
                className="border-t border-surface-100 px-4.5 py-3 transition-colors first:border-t-0 hover:bg-surface-50"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="text-[13px] font-semibold text-surface-900">
                    {ratio.label}
                  </div>
                  <div
                    className={`shrink-0 font-mono text-[13px] font-semibold tabular-nums ${
                      isUnavailable ? 'text-surface-400' : 'text-surface-900'
                    }`}
                  >
                    {formatRatio(ratio.key, ratio.value)}
                  </div>
                </div>
                <div className="mt-1 font-mono text-[11px] text-surface-500">
                  {ratio.formula}
                </div>
                {ratio.interpretation && (
                  <div className="mt-1 max-w-prose text-[12px] leading-relaxed text-surface-500">
                    {ratio.interpretation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
